// src/lib/financialReportsHelper.ts
import { reportsApi } from '../api/authApi';

export interface FinancialReportData {
  id: string;
  project_id: string;
  report_type: string;
  data: any; // Accepts both object and array-based structures
  created_at: string;
  updated_at: string;
}

// Updated to match your Supabase schema
export type ReportType = 'pnl_detail' | 'bs_detail' | 'cf_detail' | 'fin_ratios';

/**
 * Retrieve a specific financial report for a project
 */
export async function getFinancialReport(
  projectId: string, 
  reportType: ReportType
): Promise<any> {
  try {
    return await reportsApi.getByProjectAndType(projectId, reportType);
  } catch (error) {
    console.error(`Error retrieving ${reportType} report:`, error);
    return null;
  }
}

/**
 * Retrieve all financial reports for a project
 */
export async function getAllFinancialReports(
  projectId: string
): Promise<Record<string, any>> {
  try {
    const data = await reportsApi.getAllByProject(projectId);
    
    if (!data || data.length === 0) {
      console.log('No financial reports data available');
      return {
        pnl_detail: null,
        bs_detail: null,
        cf_detail: null
      };
    }

    // Convert array to keyed object with proper mapping
    const reports: Record<string, any> = {
      pnl_detail: null,
      bs_detail: null,
      cf_detail: null,
      nwc_detail: null,
      fin_ratios: null,
      pnl_percent: null
    };

    data?.forEach(report => {
      if (report.report_type === 'pnl_detail') {
        reports.pnl_detail = report;
      } else if (report.report_type === 'bs_detail') {
        reports.bs_detail = report;
      } else if (report.report_type === 'cf_detail') {
        reports.cf_detail = report;
      } else if (report.report_type === 'nwc_detail') {
        reports.nwc_detail = report;
      } else if (report.report_type === 'fin_ratios') {
        reports.fin_ratios = report;
      } else if (report.report_type === 'pnl_percent') {
        reports.pnl_percent = report;
      }
    });

    return reports;
  } catch (error) {
    console.error('Error retrieving financial reports:', error);
    return {
      pnl_detail: null,
      bs_detail: null,
      cf_detail: null,
      balance_sheet: null,
      cash_flow: null
    };
  }
}

/**
 * Get a specific line item across all months for ratio calculations
 */
export function getLineItemTimeSeries(
  reportData: FinancialReportData | null,
  lineItem: string
): Record<string, number> {
  if (!reportData || !reportData.data[lineItem]) {
    return {};
  }
  
  return reportData.data[lineItem];
}

/**
 * Get the most recent value for a line item
 */
export function getLatestLineItemValue(
  reportData: FinancialReportData | null,
  lineItem: string
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const months = Object.keys(timeSeries).sort(compareMonthYear);
  
  const latestMonth = months[months.length - 1];
  return latestMonth ? timeSeries[latestMonth] || 0 : 0;
}

/**
 * Calculate TTM (Trailing Twelve Months) for a line item
 * @deprecated Use calculateTTMForPeriodType for period-aware calculations
 */
export function calculateTTM(
  reportData: FinancialReportData | null,
  lineItem: string
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const months = Object.keys(timeSeries).sort(compareMonthYear);
  
  // Get last 12 months
  const last12Months = months.slice(-12);
  
  return last12Months.reduce((sum, month) => {
    return sum + (timeSeries[month] || 0);
  }, 0);
}

/**
 * Calculate TTM for a specific period type
 * @param reportData - Financial report data
 * @param lineItem - Line item name
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @param currentPeriod - Current period for context (optional)
 * @returns TTM value adjusted for period type
 */
export function calculateTTMForPeriodType(
  reportData: FinancialReportData | null,
  lineItem: string,
  periodType: 'monthly' | 'quarterly' | 'annual',
  currentPeriod?: string
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const periods = Object.keys(timeSeries).sort(compareMonthYear);
  
  let lookbackPeriods: string[];
  
  switch (periodType) {
    case 'quarterly':
      // Look back 4 quarters for TTM
      lookbackPeriods = periods.slice(-4);
      break;
    case 'annual':
      // Look back 1-3 years depending on available data
      lookbackPeriods = periods.slice(-3);
      break;
    default: // monthly
      // Traditional 12-month lookback
      lookbackPeriods = periods.slice(-12);
      break;
  }
  
  return lookbackPeriods.reduce((sum, period) => {
    return sum + (timeSeries[period] || 0);
  }, 0);
}

/**
 * Calculate LTM (Last Twelve Months) annualized for a shorter period
 * @deprecated Use calculateAnnualizedForPeriodType for period-aware calculations
 */
export function calculateAnnualized(
  reportData: FinancialReportData | null,
  lineItem: string,
  monthsToAnnualize: number = 1
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const months = Object.keys(timeSeries).sort(compareMonthYear);
  
  // Get the last N months
  const recentMonths = months.slice(-monthsToAnnualize);
  
  const periodTotal = recentMonths.reduce((sum, month) => {
    return sum + (timeSeries[month] || 0);
  }, 0);
  
  // Annualize: (period total / number of months) * 12
  return monthsToAnnualize > 0 ? (periodTotal / monthsToAnnualize) * 12 : 0;
}

/**
 * Calculate annualized value for a specific period type
 * @param reportData - Financial report data
 * @param lineItem - Line item name
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @param viewType - Current view type: 'monthly', 'quarterly', or 'yearly'
 * @returns Annualized value adjusted for period type
 */
export function calculateAnnualizedForPeriodType(
  reportData: FinancialReportData | null,
  lineItem: string,
  periodType: 'monthly' | 'quarterly' | 'annual',
  viewType: 'monthly' | 'quarterly' | 'yearly'
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const periods = Object.keys(timeSeries).sort(compareMonthYear);
  
  if (periods.length === 0) return 0;
  
  // Get the most recent period
  const mostRecentPeriod = periods[periods.length - 1];
  const value = timeSeries[mostRecentPeriod] || 0;
  
  // Calculate annualization factor based on period type and view type
  let annualizationFactor: number;
  
  if (periodType === 'annual') {
    // Annual data is already annualized
    annualizationFactor = 1;
  } else if (periodType === 'quarterly') {
    // Quarterly data: 4 quarters = 1 year
    annualizationFactor = viewType === 'quarterly' ? 4 : 1;
  } else { // monthly
    // Monthly data: 12 months = 1 year
    annualizationFactor = viewType === 'monthly' ? 12 : 1;
  }
  
  return value * annualizationFactor;
}

/**
 * Calculate growth rate between two periods
 * @deprecated Use calculateGrowthRateForPeriodType for period-aware calculations
 */
export function calculateGrowthRate(
  reportData: FinancialReportData | null,
  lineItem: string,
  currentMonth: string,
  previousMonth: string
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const currentValue = timeSeries[currentMonth] || 0;
  const previousValue = timeSeries[previousMonth] || 0;
  
  if (previousValue === 0) return 0;
  
  return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
}

/**
 * Calculate growth rate for a specific period type
 * @param reportData - Financial report data
 * @param lineItem - Line item name
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @param currentPeriod - Current period
 * @param previousPeriod - Previous period
 * @returns Growth rate percentage adjusted for period type
 */
export function calculateGrowthRateForPeriodType(
  reportData: FinancialReportData | null,
  lineItem: string,
  periodType: 'monthly' | 'quarterly' | 'annual',
  currentPeriod: string,
  previousPeriod: string
): number {
  const timeSeries = getLineItemTimeSeries(reportData, lineItem);
  const currentValue = timeSeries[currentPeriod] || 0;
  const previousValue = timeSeries[previousPeriod] || 0;
  
  if (previousValue === 0) return 0;
  
  // For annual data, we might want to adjust the growth calculation
  // For now, we'll use the same calculation for all period types
  return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
}

/**
 * Get all available months from a report (useful for iteration)
 */
export function getAvailableMonths(reportData: FinancialReportData | null): string[] {
  if (!reportData || !reportData.data) return [];
  
  // Get months from the first line item (all should have the same months)
  const firstLineItem = Object.keys(reportData.data)[0];
  if (!firstLineItem) return [];
  
  const months = Object.keys(reportData.data[firstLineItem]);
  
  // Sort chronologically
  return months.sort(compareMonthYear);
}

/**
 * Helper function to compare month-year strings for sorting
 */
export function compareMonthYear(a: string, b: string): number {
  const [ma, ya] = a.split('-');
  const [mb, yb] = b.split('-');
  const monthMap: Record<string, number> = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
  };
  
  const yearA = parseInt(ya) + (parseInt(ya) < 50 ? 2000 : 1900);
  const yearB = parseInt(yb) + (parseInt(yb) < 50 ? 2000 : 1900);
  
  return yearA !== yearB
    ? yearA - yearB
    : monthMap[ma] - monthMap[mb];
}

/**
 * Get available line items from a report (for debugging/mapping)
 */
export function getAvailableLineItems(reportData: FinancialReportData | null): string[] {
  if (!reportData || !reportData.data) return [];
  
  return Object.keys(reportData.data);
}

/**
 * Check if a line item exists in any of the provided reports
 */
export function findLineItemInReports(
  lineItem: string,
  reports: Record<string, any>
): any {
  for (const report of Object.values(reports)) {
    if (report?.data?.[lineItem]) {
      return report;
    }
  }
  return null;
}

/**
 * Get value for a line item from the best available report
 */
export function getLineItemValue(
  lineItem: string,
  period: string,
  reports: Record<string, any>
): number {
  const report = findLineItemInReports(lineItem, reports);
  if (!report?.data?.[lineItem]?.[period]) return 0;
  
  return report.data[lineItem][period] || 0;
}

/**
 * Get TTM value for a line item from the best available report
 */
export function getTTMLineItemValue(
  lineItem: string,
  reports: Record<string, any>
): number {
  const report = findLineItemInReports(lineItem, reports);
  if (!report) return 0;
  
  return calculateTTM(report, lineItem);
}

/**
 * Utility to get average balance between current and previous period
 * Useful for balance sheet ratios that need average balances
 */
export function getAverageBalance(
  lineItem: string,
  currentPeriod: string,
  reports: Record<string, any>
): number {
  const report = findLineItemInReports(lineItem, reports);
  if (!report) return 0;
  
  const timeSeries = getLineItemTimeSeries(report, lineItem);
  const months = Object.keys(timeSeries).sort(compareMonthYear);
  const currentIndex = months.indexOf(currentPeriod);
  
  if (currentIndex <= 0) return timeSeries[currentPeriod] || 0;
  
  const previousPeriod = months[currentIndex - 1];
  const currentValue = timeSeries[currentPeriod] || 0;
  const previousValue = timeSeries[previousPeriod] || 0;
  
  return (currentValue + previousValue) / 2;
}

// Export common line item mappings for easy reference
export const COMMON_LINE_ITEMS = {
  // P&L Items
  TOTAL_REVENUE: 'Total Revenue',
  GROSS_PROFIT: 'Gross Profit',
  TOTAL_OPERATING_EXPENSES: 'Total Operating Expenses',
  OPERATING_INCOME: 'Operating Income (Loss)',
  NET_INCOME: 'Net Income (Loss)',
  EBITDA: 'EBITDA',
  INTEREST_EXPENSE: 'Interest Expense (Income)',
  INCOME_TAX_EXPENSE: 'Income Tax Expense',
  DEPRECIATION: 'Depreciation',
  AMORTIZATION: 'Amortization',
  
  // Balance Sheet Items
  TOTAL_CURRENT_ASSETS: 'Total Current Assets',
  TOTAL_CURRENT_LIABILITIES: 'Total Current Liabilities',
  TOTAL_ASSETS: 'Total Assets',
  TOTAL_LIABILITIES: 'Total Liabilities',
  TOTAL_EQUITY: 'Total Equity',
  CASH: 'Cash',
  INVENTORY: 'Inventory',
  ACCOUNTS_RECEIVABLE: 'Trade Accounts Receivable',
  ACCOUNTS_PAYABLE: 'Trade Accounts Payable',
  TOTAL_DEBT: 'Total Debt',
  
  // Cash Flow Items
  OPERATING_CASH_FLOW: 'Net Cash from Operating Activities',
  INVESTING_CASH_FLOW: 'Net Cash from Investing Activities',
  FINANCING_CASH_FLOW: 'Net Cash from Financing Activities',
} as const;

/**
 * Extract Net Cash from Operating Activities from cf_detail.sections for a given period
 * @param cfDetail The cf_detail object (array-based structure, not strict FinancialReportData)
 * @param period The period string (e.g., 'Jan-24')
 * @returns The value for Net Cash from Operating Activities for the given period, or 0 if not found
 */
export function getOperatingCashFlowFromCFDetail(
  cfDetail: any,
  period: string
): number {
  if (!cfDetail || !cfDetail.sections || !Array.isArray(cfDetail.sections) || !cfDetail.periods) return 0;
  const section = cfDetail.sections.find((s: any) => s.key === 'net_cash_from_operating_activities');
  if (!section || !Array.isArray(section.line_items)) return 0;
  const periodIdx = cfDetail.periods.indexOf(period);
  // Debug logs
  console.log('[OCF Debug] cfDetail.periods:', cfDetail.periods);
  console.log('[OCF Debug] Requested period:', period);
  console.log('[OCF Debug] Section:', section);
  console.log('[OCF Debug] Period index:', periodIdx);
  if (periodIdx === -1) return 0;
  // Sum all line item values for this period
  const sum = section.line_items.reduce((sum: number, item: any) => {
    if (!item.values || !Array.isArray(item.values)) return sum;
    const val = item.values[periodIdx];
    return sum + (typeof val === 'number' ? val : 0);
  }, 0);
  console.log('[OCF Debug] OCF sum for period', period, ':', sum);
  return sum;
}